import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import ProgressiveImage from '../../components/common/ProgressiveImage';
import { courseService, enrollmentService } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, Filter, BookOpen, Database, Compass, LayoutGrid, List } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import ListPagination from '../../components/list/ListPagination';
import StatusPill from '../../components/list/StatusPill';
import { ShortCardGrid, ListSection } from '../../components/list/ShortCard';
import { CourseShortCard } from '../../components/list/ShortCardVariants';
import { DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';
import { hasAccessibleEnrollment } from '../../utils/enrollmentHelpers';
import '../../styles/course-list.css';

function CourseList() {
    const [courses, setCourses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const { user, isAuthenticated } = useAuth();
    const [activeTab, setActiveTab] = useState('All');

    const fetchCoursesAndEnrollments = useCallback(async () => {
        setLoading(true);
        try {
            const [coursesRes, enrollmentsRes] = await Promise.all([
                courseService.getCourses(),
                isAuthenticated ? enrollmentService.getEnrollments({ student: user.id }) : Promise.resolve({ data: [] }),
            ]);

            const coursesData = coursesRes.data.results || coursesRes.data;
            setCourses(coursesData);

            const enrollmentsData = enrollmentsRes.data.results || enrollmentsRes.data || [];
            if (isAuthenticated) {
                setEnrollments(enrollmentsData);
            }
        } catch (err) {
            console.error('Failed to load data:', err);
            toast.error('Failed to load courses');
        } finally {
            setLoading(false);
        }
    }, [user, isAuthenticated]);

    useEffect(() => {
        fetchCoursesAndEnrollments();
    }, [fetchCoursesAndEnrollments]);

    const handleEnroll = async (id) => {
        if (!isAuthenticated) return toast.error('Please login to enroll!');
        try {
            await enrollmentService.createEnrollment(id);
            toast.success('Successfully enrolled in course!');
            await fetchCoursesAndEnrollments();
        } catch (err) {
            console.error('Enrollment error:', err);
            toast.error('Enrollment failed or already pending approval');
        }
    };

    const filteredCourses = useMemo(
        () => courses.filter((c) => c.title.toLowerCase().includes(searchTerm.toLowerCase())),
        [courses, searchTerm]
    );

    const enrolledCourses = useMemo(() => {
        if (!isAuthenticated) return [];
        return filteredCourses.filter((c) => hasAccessibleEnrollment(enrollments, c.id));
    }, [filteredCourses, enrollments, isAuthenticated]);

    const discoverCourses = useMemo(() => {
        if (!isAuthenticated) return filteredCourses;
        return filteredCourses.filter((c) => !hasAccessibleEnrollment(enrollments, c.id));
    }, [filteredCourses, enrollments, isAuthenticated]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, activeTab]);

    const pagedDiscover = useMemo(
        () => discoverCourses.slice((page - 1) * pageSize, page * pageSize),
        [discoverCourses, page, pageSize]
    );

    const pagedAll = useMemo(
        () => filteredCourses.slice((page - 1) * pageSize, page * pageSize),
        [filteredCourses, page, pageSize]
    );

    const renderShortCard = (course, index) => {
        const enrollment = enrollments.find((e) => e.course === course.id);
        return (
            <CourseShortCard
                key={course.id}
                course={course}
                enrollment={enrollment}
                index={index}
                to={`/courses/${course.id}`}
            />
        );
    };

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text font-sans selection:bg-theme-primary/30">
            <Navbar />

            <section className="course-catalog-hero">
                <div className="course-catalog-hero-grid">
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="course-catalog-hero-copy"
                    >
                        <div className="course-catalog-hero-kicker">
                            <Compass size={16} aria-hidden="true" />
                            <span>Explore the academy</span>
                        </div>

                        <h1>
                            Master the future of{' '}
                            <span>artificial intelligence</span>
                        </h1>

                        <p>
                            Build production-ready skills through focused courses for
                            modern teams, professionals, and emerging technologists.
                        </p>
                    </motion.div>

                    <motion.figure
                        initial={{ opacity: 0, scale: 0.97, x: 24 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        transition={{ delay: 0.12 }}
                        className="course-catalog-hero-visual"
                    >
                        <img
                            src="/courses_hero.webp"
                            alt="A connected artificial intelligence learning network"
                            loading="eager"
                            decoding="async"
                            fetchPriority="high"
                            width="600"
                            height="400"
                        />
                        <figcaption>
                            <span>AI &amp; Data</span>
                            Connected learning pathways
                        </figcaption>
                    </motion.figure>
                </div>
            </section>

            <main className="max-w-7xl mx-auto px-6 py-16 relative z-10 min-h-screen">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12 bg-theme-card border border-theme-border p-4 rounded-3xl shadow-sm backdrop-blur-xl"
                >
                    <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
                        {['All', 'Development', 'AI & Data', 'Business', 'Design'].map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setActiveTab(cat)}
                                className={`px-6 py-3 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${activeTab === cat ? 'bg-theme-primary text-theme-on-primary shadow-md' : 'bg-transparent text-theme-muted hover:bg-theme-bg'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto relative">
                        <Search size={18} className="absolute left-4 text-theme-muted" />
                        <input
                            type="text"
                            placeholder="Search curriculum..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-theme-bg border border-theme-border text-theme-text rounded-xl pl-12 pr-4 py-3 w-full md:w-64 focus:outline-none focus:border-theme-primary transition-colors"
                        />
                        <button type="button" className="bg-theme-bg border border-theme-border p-3 rounded-xl hover:border-theme-primary transition-colors">
                            <Filter size={18} className="text-theme-muted" />
                        </button>
                        <div className="ux-list-view-toggle">
                            <button type="button" className={viewMode === 'grid' ? 'is-active' : ''} onClick={() => setViewMode('grid')} aria-label="Grid view">
                                <LayoutGrid size={18} />
                            </button>
                            <button type="button" className={viewMode === 'list' ? 'is-active' : ''} onClick={() => setViewMode('list')} aria-label="List view">
                                <List size={18} />
                            </button>
                        </div>
                    </div>
                </motion.div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-12 h-12 rounded-full border-4 border-theme-border border-t-theme-primary animate-spin mb-4" />
                        <span className="text-theme-muted font-medium">Assembling curriculums...</span>
                    </div>
                ) : filteredCourses.length === 0 ? (
                    <div className="py-20 text-center flex flex-col items-center">
                        <Database size={48} className="text-theme-border mb-4" />
                        <h3 className="text-2xl font-bold text-theme-text mb-2">No modules found</h3>
                        <p className="text-theme-muted">We couldn&apos;t locate any courses matching your search criteria.</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    <>
                        {isAuthenticated && enrolledCourses.length > 0 && (
                            <ListSection
                                title="Continue learning"
                                description="Pick up where you left off."
                            >
                                <div className="ux-short-scroll">
                                    {enrolledCourses.slice(0, 8).map((course, index) => renderShortCard(course, index))}
                                </div>
                            </ListSection>
                        )}

                        <ListSection
                            title={isAuthenticated ? 'Discover new courses' : 'Popular on Learnly'}
                            description="Short programs designed for busy professionals."
                        >
                            <ShortCardGrid>
                                {pagedDiscover.map((course, index) => renderShortCard(course, index))}
                            </ShortCardGrid>
                        </ListSection>

                        <ListPagination
                            page={page}
                            pageSize={pageSize}
                            totalCount={discoverCourses.length || filteredCourses.length}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                            pageSizes={[8, 12, 24]}
                        />
                    </>
                ) : (
                    <>
                        <DataRowList>
                            {pagedAll.map((course, index) => {
                                const enrollment = enrollments.find((e) => e.course === course.id);
                                return (
                                    <DataRow
                                        key={course.id}
                                        index={index}
                                        leading={(
                                            <div className="ux-row-thumb" style={{ width: '48px', height: '48px', overflow: 'hidden', borderRadius: '8px' }}>
                                                {course.thumbnail ? (
                                                    <ProgressiveImage src={course.thumbnail} alt={course.title} aspectRatio="1/1" />
                                                ) : (
                                                    <BookOpen size={20} />
                                                )}
                                            </div>
                                        )}
                                        title={course.title}
                                        subtitle={course.description || course.instructor_name || 'Explore this course'}
                                        meta={(
                                            <>
                                                {course.level && <span>{course.level}</span>}
                                                {course.duration && <span>{course.duration} min</span>}
                                            </>
                                        )}
                                        badges={enrollment?.status ? <StatusPill status={enrollment.status} label={enrollment.status} /> : null}
                                        actions={(
                                            <>
                                                <Link to={`/courses/${course.id}`} className="ux-btn-sm ux-btn-ghost" style={{ textDecoration: 'none' }}>View</Link>
                                                {!enrollment && isAuthenticated && (
                                                    <button type="button" className="ux-btn-sm ux-btn-primary" onClick={() => handleEnroll(course.id)}>Enroll</button>
                                                )}
                                            </>
                                        )}
                                    />
                                );
                            })}
                        </DataRowList>
                        <ListPagination
                            page={page}
                            pageSize={pageSize}
                            totalCount={filteredCourses.length}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}

export default CourseList;
